import { Button, Tooltip } from "@fluentui/react-components";
import {
  Cell,
  CellTemplate,
  Compatible,
  Uncertain,
  UncertainCompatible,
  getCellProperty,
  inNumericKey,
  isAllowedOnNumberTypingKey,
  isNavigationKey,
  isNumpadNumericKey,
  keyCodes,
} from "@silevis/reactgrid";
import * as React from "react";
import { DismissFilled, FlashAutoRegular } from "@fluentui/react-icons";
import { useRemindersStoreState } from "store/RemindersStore";
// NOTE: all modules imported below may be imported from '@silevis/reactgrid'

export interface ClearedAmountCell extends Cell {
  type: "clearedamount";
  value: number;
  totalToBeCleared?: number;
  getClearanceDataTotal?: () => number;
  premiumDebt?: number;
  format?: Intl.NumberFormat;
  validator?: (value: number) => boolean;
  nanToZero?: boolean;
  hideZero?: boolean;
  t: any;
  selected?: boolean;
  highlighted?: boolean;
  errorMessage?: string;
}

export class ClearedAmountCellTemplate
  implements CellTemplate<ClearedAmountCell>
{
  getCompatibleCell(
    uncertainCell: Uncertain<ClearedAmountCell>
  ): Compatible<ClearedAmountCell> {
    let value: number;
    let getClearanceDataTotal: () => number;
    let totalToBeCleared: number;
    let premiumDebt: number;
    let t: any;

    t = getCellProperty(uncertainCell, "t", "function");
    try {
      value = getCellProperty(uncertainCell, "value", "number");
    } catch (error) {
      value = NaN;
    }
    try {
      totalToBeCleared = getCellProperty(
        uncertainCell,
        "totalToBeCleared",
        "number"
      );
    } catch (error) {
      totalToBeCleared = NaN;
    }
    try {
      getClearanceDataTotal = getCellProperty(
        uncertainCell,
        "getClearanceDataTotal",
        "function"
      );
    } catch (error) {
      getClearanceDataTotal = undefined;
    }

    try {
      premiumDebt = getCellProperty(uncertainCell, "premiumDebt", "number");
    } catch (error) {
      premiumDebt = NaN;
    }

    const numberFormat =
      uncertainCell.format || new Intl.NumberFormat(window.navigator.language);
    const displayValue =
      uncertainCell.nanToZero && Number.isNaN(value) ? 0 : value;
    const text =
      Number.isNaN(displayValue) ||
      (uncertainCell.hideZero && displayValue === 0)
        ? ""
        : numberFormat.format(displayValue);

    return {
      ...uncertainCell,
      value: displayValue,
      text,
      t,
      getClearanceDataTotal,
      totalToBeCleared,
      premiumDebt,
    };
  }

  handleKeyDown(
    cell: Compatible<ClearedAmountCell>,
    keyCode: number,
    ctrl: boolean,
    shift: boolean,
    alt: boolean
  ): { cell: Compatible<ClearedAmountCell>; enableEditMode: boolean } {
    if (isNumpadNumericKey(keyCode)) keyCode -= 48;
    const char = String.fromCharCode(keyCode);
    if (
      !ctrl &&
      !alt &&
      !shift &&
      (inNumericKey(keyCode) || isAllowedOnNumberTypingKey(keyCode))
    ) {
      const value = Number(char);
      if (Number.isNaN(value) && isAllowedOnNumberTypingKey(keyCode))
        return {
          cell: { ...this.getCompatibleCell({ ...cell, value }), text: char },
          enableEditMode: true,
        };
      return {
        cell: this.getCompatibleCell({ ...cell, value }),
        enableEditMode: true,
      };
    }
    return {
      cell,
      enableEditMode:
        keyCode === keyCodes.POINTER || keyCode === keyCodes.ENTER,
    };
  }

  update(
    cell: Compatible<ClearedAmountCell>,
    cellToMerge: UncertainCompatible<ClearedAmountCell>
  ): Compatible<ClearedAmountCell> {
    return this.getCompatibleCell({ ...cell, value: cellToMerge.value });
  }

  private getTextFromCharCode = (cellText: string): string => {
    switch (cellText.charCodeAt(0)) {
      case keyCodes.DASH:
      case keyCodes.FIREFOX_DASH:
      case keyCodes.SUBTRACT:
        return "-";
      case keyCodes.COMMA:
        return ",";
      case keyCodes.PERIOD:
      case keyCodes.DECIMAL:
        return ".";
      default:
        return cellText;
    }
  };

  getClassName(
    cell: Compatible<ClearedAmountCell>,
    isInEditMode: boolean
  ): string {
    const isValid = cell.validator?.(cell.value) ?? true;
    const className =
      (cell.className ? cell.className : "") +
      (cell.selected ? " rg-selected" : "") +
      (cell.highlighted ? " highlighted-selected-row" : "");
    return `${!isValid ? "rg-invalid" : ""} ${className}`;
  }

  render(
    cell: Compatible<ClearedAmountCell>,
    isInEditMode: boolean,
    onCellChanged: (
      cell: Compatible<ClearedAmountCell>,
      commit: boolean
    ) => void
  ): React.ReactNode {
    if (!isInEditMode) {
      const isValid = cell.validator?.(cell.value) ?? true;
      const textToDisplay =
        !isValid && cell.errorMessage ? cell.errorMessage : cell.text;
      return (
        <div style={{ display: "flex", width: "100%" }}>
          <Tooltip content={cell.t("Select")} relationship="label">
            <Button
              appearance="subtle"
              size="small"
              icon={<FlashAutoRegular />}
              onClick={(e) => {
                let value =
                  cell.totalToBeCleared - cell.getClearanceDataTotal();
                if (value < 0) value = 0;
                else if (value > cell.premiumDebt) value = cell.premiumDebt;

                onCellChanged(
                  this.getCompatibleCell({
                    ...cell,
                    value,
                  }),
                  true
                );
              }}
            ></Button>
          </Tooltip>
          <Tooltip content={cell.t("Clear")} relationship="label">
            <Button
              appearance="subtle"
              size="small"
              icon={<DismissFilled />}
              onClick={(e) => {
                onCellChanged(
                  this.getCompatibleCell({
                    ...cell,
                    value: 0,
                  }),
                  true
                );
              }}
            ></Button>
          </Tooltip>
          <div style={{ flexGrow: 1, textAlign: "right" }}>{textToDisplay}</div>
        </div>
      );
    }

    const locale = cell.format
      ? cell.format.resolvedOptions().locale
      : window.navigator.languages[0];
    const format = new Intl.NumberFormat(locale, {
      useGrouping: false,
      maximumFractionDigits: 20,
    });

    return (
      <input
        inputMode="decimal"
        ref={(input) => {
          if (input) {
            input.focus();
            input.setSelectionRange(input.value.length, input.value.length);
            input.classList.add("cell-input");
          }
        }}
        defaultValue={
          Number.isNaN(cell.value)
            ? this.getTextFromCharCode(cell.text)
            : format.format(cell.value)
        }
        onChange={(e) =>
          onCellChanged(
            this.getCompatibleCell({
              ...cell,
              value: parseFloat(e.currentTarget.value.replace(/,/g, ".")),
            }),
            false
          )
        }
        onBlur={(e) =>
          onCellChanged(
            this.getCompatibleCell({
              ...cell,
              value: parseFloat(e.currentTarget.value.replace(/,/g, ".")),
            }),
            true
          )
        }
        onKeyDown={(e) => {
          if (
            inNumericKey(e.keyCode) ||
            isNavigationKey(e.keyCode) ||
            isAllowedOnNumberTypingKey(e.keyCode)
          )
            e.stopPropagation();
          if (
            (!inNumericKey(e.keyCode) &&
              !isNavigationKey(e.keyCode) &&
              !isAllowedOnNumberTypingKey(e.keyCode)) ||
            e.shiftKey
          )
            e.preventDefault();
        }}
        onCopy={(e) => e.stopPropagation()}
        onCut={(e) => e.stopPropagation()}
        onPaste={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
      />
    );
  }
}
